import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import BackButton from "@/components/BackButton";
import { toast } from "sonner";
import { Lock, Mail, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Accesso effettuato");
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 grain overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20 blur-sm"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1645400379459-f6fd3d963fd4?crop=entropy&cs=srgb&fm=jpg&q=85)" }}
      />
      <div className="absolute inset-0 bg-zinc-950/80" />
      <div className="relative z-10 w-full max-w-md border border-zinc-800 bg-zinc-900/90 backdrop-blur-xl p-8">
        <BackButton className="mb-6" />
        <ShieldCheck className="h-10 w-10 text-yellow-400 text-glow-yellow mb-4" />
        <h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-zinc-50">Area Admin</h1>
        <p className="mt-2 text-sm text-zinc-400">Accedi per gestire campionati, Gran Premi e classifiche.</p>

        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400">Email</label>
            <div className="mt-2 flex items-center gap-2 border border-zinc-700 bg-zinc-950 px-3 focus-within:border-yellow-400 transition-colors duration-200">
              <Mail className="h-4 w-4 text-zinc-500" />
              <input
                type="email"
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-transparent py-3 text-zinc-100 outline-none placeholder:text-zinc-600"
                placeholder="admin@email.com"
              />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-zinc-400">Password</label>
            <div className="mt-2 flex items-center gap-2 border border-zinc-700 bg-zinc-950 px-3 focus-within:border-yellow-400 transition-colors duration-200">
              <Lock className="h-4 w-4 text-zinc-500" />
              <input
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-transparent py-3 text-zinc-100 outline-none placeholder:text-zinc-600"
                placeholder="••••••••"
              />
            </div>
          </div>
          {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-yellow-400 text-zinc-950 font-bold text-sm tracking-[0.15em] uppercase py-4 hover:bg-yellow-300 transition-colors duration-200 disabled:opacity-50"
          >
            {loading ? "Accesso…" : "Entra"}
          </button>
        </form>
      </div>
    </div>
  );
}
